import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  ensureIntercomBooted,
  openSupportChat as openSupportChatUnsafe,
  shutdownIntercom,
  updateIntercom,
} from "utils/intercom";

const RETRY_INTERVAL_MS = 250;
const RETRY_ATTEMPTS = 20;

export const useIntercomLifecycle = ({
  user,
  customLauncherSelector,
  hideDefaultLauncher = false,
  identity,
  enabled = true,
} = {}) => {
  const location = useLocation();
  const options = useMemo(
    () => ({
      customLauncherSelector,
      hideDefaultLauncher,
      identity,
    }),
    [customLauncherSelector, hideDefaultLauncher, identity]
  );

  const latestStateRef = useRef({ user, options });
  const wasEnabledRef = useRef(false);
  const lastRouteRef = useRef("");

  useEffect(() => {
    latestStateRef.current = { user, options };
  }, [user, options]);

  useEffect(() => {
    if (!enabled) {
      if (wasEnabledRef.current) {
        shutdownIntercom();
        wasEnabledRef.current = false;
        lastRouteRef.current = "";
      }

      return undefined;
    }

    let isCancelled = false;
    let retryId;

    const syncIntercom = () => {
      if (isCancelled) return true;

      const { user: latestUser, options: latestOptions } = latestStateRef.current;
      const currentRoute = `${location.pathname}${location.search}`;

      if (!ensureIntercomBooted(latestUser, latestOptions)) {
        return false;
      }

      const shouldUpdate = lastRouteRef.current !== currentRoute || !wasEnabledRef.current;

      if (shouldUpdate && !updateIntercom(latestUser, latestOptions)) {
        return false;
      }

      lastRouteRef.current = currentRoute;
      wasEnabledRef.current = true;

      return true;
    };

    if (syncIntercom()) {
      return undefined;
    }

    let attempts = 0;

    retryId = window.setInterval(() => {
      attempts += 1;

      if (syncIntercom() || attempts >= RETRY_ATTEMPTS) {
        window.clearInterval(retryId);
      }
    }, RETRY_INTERVAL_MS);

    return () => {
      isCancelled = true;

      if (retryId) {
        window.clearInterval(retryId);
      }
    };
  }, [enabled, location.pathname, location.search]);
};

const useIntercom = ({
  user,
  customLauncherSelector,
  hideDefaultLauncher = false,
  identity,
  autoBoot = true,
  enabled = true,
} = {}) => {
  const options = useMemo(
    () => ({
      customLauncherSelector,
      hideDefaultLauncher,
      identity,
    }),
    [customLauncherSelector, hideDefaultLauncher, identity]
  );

  const latestStateRef = useRef({ user, options });

  useEffect(() => {
    latestStateRef.current = { user, options };
  }, [user, options]);

  useIntercomLifecycle({
    user,
    customLauncherSelector,
    hideDefaultLauncher,
    identity,
    enabled: autoBoot && enabled,
  });

  const openSupportChat = useCallback(() => {
    const tryOpen = () => {
      const { user: latestUser, options: latestOptions } = latestStateRef.current;
      return openSupportChatUnsafe(latestUser, latestOptions);
    };

    if (tryOpen()) {
      return true;
    }

    let attempts = 0;

    const retryId = window.setInterval(() => {
      attempts += 1;

      if (tryOpen() || attempts >= RETRY_ATTEMPTS) {
        window.clearInterval(retryId);
      }
    }, RETRY_INTERVAL_MS);

    return false;
  }, []);

  return {
    openSupportChat,
  };
};

export default useIntercom;
